import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supportAPI } from '../services/api';

// Open the Report Issue modal from anywhere in the app:
//   window.dispatchEvent(new Event('openReportIssue'))
// Used by Sidebar's "Report Issue" nav item (and any other entry point).
export const REPORT_ISSUE_EVENT = 'openReportIssue';

const CATEGORIES = [
  'Payment Issue',
  'Login / Account',
  'Feature Not Working',
  'Bug Report',
  'Feature Request',
  'Billing & Subscription',
  'Other',
];

export default function HelpWidget() {
  const { user } = useAuth();
  const fileRef  = useRef();

  const [open,    setOpen]    = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [image,   setImage]   = useState(null);   // { file, preview }
  const [dragOver, setDragOver] = useState(false);

  const [form, setForm] = useState({
    category: CATEGORIES[0],
    subject:  '',
    message:  '',
  });

  // Listen for external opens (from Sidebar's "Report Issue" item)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(REPORT_ISSUE_EVENT, handler);
    return () => window.removeEventListener(REPORT_ISSUE_EVENT, handler);
  }, []);

  const handleChange = e =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  /* ── Image handling ── */
  const handleImageFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = e => setImage({ file, preview: e.target.result });
    reader.readAsDataURL(file);
  };

  const handleFileInput  = e => handleImageFile(e.target.files[0]);
  const handleDrop       = e => { e.preventDefault(); setDragOver(false); handleImageFile(e.dataTransfer.files[0]); };
  const handleDragOver   = e => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave  = ()  => setDragOver(false);
  const removeImage      = ()  => { setImage(null); if (fileRef.current) fileRef.current.value = ''; };

  const handleClose = () => {
    setOpen(false);
    setSuccess(false);
    setImage(null);
    setForm(f => ({ ...f, subject: '', message: '' }));
    if (fileRef.current) fileRef.current.value = '';
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) return;
    setLoading(true);

    try {
      await supportAPI.create({
        name:        user?.name  || '',
        email:       user?.email || '',
        category:    form.category,
        subject:     form.subject,
        description: form.message,
        imageBase64: image ? image.preview : null,
      });
      setSuccess(true);
      setTimeout(handleClose, 3500);
    } catch (err) {
      alert('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Modal opens via window event 'openReportIssue' (see REPORT_ISSUE_EVENT)
          — Sidebar's "Report Issue" nav item is the primary trigger.
          Bottom-right floating button removed 2026-05-21 to free that spot
          for the upcoming chatbot. */}

      {/* ── Modal Overlay — slide-out from LEFT, anchored near sidebar ── */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 1000, display: 'flex', alignItems: 'stretch',
            justifyContent: 'flex-start', padding: 0,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '0 20px 20px 0',
              width: '440px', maxWidth: '92vw',
              boxShadow: '20px 0 60px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              height: '100vh',
              animation: 'slideInLeft 0.25s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0f1117, #1a2535)',
              padding: '1.25rem 1.5rem', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>📝</span>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1rem', color: 'white' }}>
                    Report an Issue
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: '0.2rem' }}>
                  Bug, feedback, or request — we respond within 24 hours
                </div>
              </div>
              <button onClick={handleClose}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', width: 32, height: 32, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ✕
              </button>
            </div>

            {/* Scrollable Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {success ? (
                <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                  <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>✅</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f1117', marginBottom: '0.5rem' }}>
                    Ticket Submitted!
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.6 }}>
                    Your support ticket has been raised. We'll get back to you within 24 hours.
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                  {/* Category */}
                  <div>
                    <label style={labelStyle}>Category</label>
                    <select name="category" value={form.category} onChange={handleChange}
                      className="form-input" style={{ fontSize: '0.82rem', cursor: 'pointer' }}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={labelStyle}>Subject <span style={{ color: '#ef4444' }}>*</span></label>
                    <input name="subject" value={form.subject} onChange={handleChange}
                      placeholder="Brief description of your issue"
                      className="form-input" style={{ fontSize: '0.82rem' }} required />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={labelStyle}>Description <span style={{ color: '#ef4444' }}>*</span></label>
                    <textarea name="message" value={form.message} onChange={handleChange}
                      placeholder="Describe your issue in detail. Include steps to reproduce if it's a bug..."
                      rows={5} className="form-input"
                      style={{ fontSize: '0.82rem', resize: 'vertical', minHeight: '120px', lineHeight: 1.6 }}
                      required />
                  </div>

                  {/* Screenshot Upload */}
                  <div>
                    <label style={labelStyle}>Screenshot <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>

                    {!image ? (
                      <div
                        onClick={() => fileRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        style={{
                          border: `2px dashed ${dragOver ? '#1a7a4a' : '#e5e7eb'}`,
                          borderRadius: '10px', padding: '1.25rem',
                          textAlign: 'center', cursor: 'pointer',
                          background: dragOver ? '#eef7f2' : '#f9fafb',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ fontSize: '1.75rem', marginBottom: '0.4rem' }}>📸</div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.2rem' }}>
                          Click to upload or drag & drop
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                          PNG, JPG, GIF up to 5MB
                        </div>
                        <input
                          ref={fileRef} type="file" accept="image/*"
                          onChange={handleFileInput}
                          style={{ display: 'none' }}
                        />
                      </div>
                    ) : (
                      <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                        <img
                          src={image.preview} alt="screenshot"
                          style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', display: 'block' }}
                        />
                        {/* Overlay with file info */}
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          background: 'rgba(0,0,0,0.6)', padding: '0.5rem 0.75rem',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                          <span style={{ fontSize: '0.72rem', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            📎 {image.file.name} ({(image.file.size / 1024).toFixed(0)} KB)
                          </span>
                          <button type="button" onClick={removeImage}
                            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', padding: '0.2rem 0.5rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading || !form.subject.trim() || !form.message.trim()}
                    style={{
                      padding: '0.8rem', border: 'none', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #1a7a4a, #4dba7a)',
                      color: 'white', fontWeight: 700, fontSize: '0.9rem',
                      cursor: (loading || !form.subject.trim() || !form.message.trim()) ? 'not-allowed' : 'pointer',
                      opacity: (loading || !form.subject.trim() || !form.message.trim()) ? 0.6 : 1,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {loading ? '⏳ Submitting...' : '📨 Submit Ticket'}
                  </button>

                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'center' }}>
                    We reply within 24h · Your ticket is stored securely
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const labelStyle = {
  fontSize: '0.75rem', fontWeight: 600, color: '#374151',
  display: 'block', marginBottom: '0.35rem',
};
